import { FC, Fragment, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import ErrorIcon from '@mui/icons-material/Error';

import {
    cardBrandName,
    formatCardExpiryLong,
    savedCardAccessibleLabel,
    type SavedCustomerCard
} from '../../api/customerCards/cardHelpers';
import CardTile from './CardTile';

/** The radio value of "new card on the payment page". */
export const PAGE_OPTION = 'page';

type Props = {
    cards: SavedCustomerCard[];
    /** {@link PAGE_OPTION} or the chosen card's id. */
    value: string;
    onChange: (value: string) => void;
    pageOptionDisabled?: boolean;
    /** Every card row disabled (no payer, no permission, agent payer, form disabled). */
    cardsDisabled?: boolean;
    /** Why this one card cannot be chosen, or '' — shown as its tooltip. */
    cardDisabledReason: (card: SavedCustomerCard) => string;
};

// Row geometry, shared by every row and the selected row's follow-up controls.
const ROW_PX = 12;
const RADIO_COL = 30;
const COL_GAP = 12;
/** CardTile `medium`. */
const TILE_WIDTH = 68;
const TILE_HEIGHT = 43;
/** Three body2 lines — every row is this tall, the two-line "new card" row included. */
const TEXT_MIN_HEIGHT = 60;

type RowProps = {
    value: string;
    selected: boolean;
    disabled: boolean;
    tooltip?: string;
    ariaLabel: string;
    tile: ReactNode;
    lines: ReactNode;
};

const OptionRow: FC<RowProps> = ({ value, selected, disabled, tooltip = '', ariaLabel, tile, lines }) => (
    <Box
        sx={(theme) => {
            const dark = theme.palette.mode === 'dark';
            return {
                border: '1px solid',
                borderColor: selected ? alpha(theme.palette.primary.main, dark ? 0.7 : 0.45) : 'transparent',
                bgcolor: selected ? alpha(theme.palette.primary.main, dark ? 0.18 : 0.07) : 'transparent',
                borderRadius: '8px',
                transition: 'background-color 120ms ease, border-color 120ms ease',
                ...(!selected && !disabled && { '&:hover': { bgcolor: 'action.hover' } }),
                '&:has(input:focus-visible)': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '1px'
                }
            };
        }}
    >
        <Tooltip title={tooltip} placement="top-start" describeChild>
            <Box
                component="label"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: `${COL_GAP}px`,
                    px: `${ROW_PX}px`,
                    py: 1,
                    cursor: disabled ? 'not-allowed' : 'pointer'
                }}
            >
                <Box sx={{ flex: 'none', width: RADIO_COL }}>
                    <Radio
                        value={value}
                        size="small"
                        disabled={disabled}
                        inputProps={{ 'aria-label': tooltip ? `${ariaLabel}. ${tooltip}` : ariaLabel }}
                        sx={{ p: 0.5 }}
                    />
                </Box>
                <Box sx={{ flex: 'none', display: 'flex', opacity: disabled ? 0.6 : 1 }}>{tile}</Box>
                {/* The radio's aria-label names the option; these lines are what the eye reads. */}
                <Box
                    aria-hidden
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: TEXT_MIN_HEIGHT,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    {lines}
                </Box>
            </Box>
        </Tooltip>
    </Box>
);

const lineSx = { display: 'block', lineHeight: '20px' } as const;

/** Holder name, masked number, expiry — the three lines beside a saved card's tile. */
const CardLines: FC<{ card: SavedCustomerCard; muted: boolean }> = ({ card, muted }) => {
    const digits = (card.last4 || '').replace(/\D/g, '').slice(-4);
    const holder = (card.cardHolderName || '').trim();
    return (
        <>
            <Typography
                variant="body2"
                component="span"
                noWrap
                sx={{ ...lineSx, fontWeight: 600, opacity: muted ? 0.6 : 1 }}
            >
                {/* No holder on the transaction: the brand is the next best name. */}
                {holder || cardBrandName(card.brand)}
            </Typography>
            <Typography
                variant="body2"
                component="span"
                noWrap
                sx={{
                    ...lineSx,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '0.02em',
                    opacity: muted ? 0.6 : 1
                }}
            >
                {digits ? `•••• ${digits}` : '••••'}
            </Typography>
            {card.isExpired ? (
                <Typography
                    variant="body2"
                    component="span"
                    color="error"
                    noWrap
                    sx={{ ...lineSx, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                >
                    <ErrorIcon sx={{ fontSize: 16 }} />
                    Expired {formatCardExpiryLong(card)}
                </Typography>
            ) : (
                <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                    noWrap
                    sx={{ ...lineSx, opacity: muted ? 0.6 : 1 }}
                >
                    Expires {formatCardExpiryLong(card)}
                </Typography>
            )}
        </>
    );
};

const NewCardTile: FC = () => (
    <Box
        aria-hidden
        sx={{
            flex: 'none',
            width: TILE_WIDTH,
            height: TILE_HEIGHT,
            borderRadius: '6px',
            border: '1.5px dashed',
            borderColor: 'text.disabled',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}
    >
        <AddCardOutlinedIcon sx={{ fontSize: 26, width: 26, height: 26 }} />
    </Box>
);

/**
 * The card choice on a receipt, as a selectable list: "new card on the payment
 * page" first, then the payer's saved cards — each a card tile with the holder,
 * the masked number and the expiry beside it. The selected row is highlighted;
 * its follow-up controls (number of payments, etc.) are the caller's, rendered
 * below the whole list rather than nested inside the row.
 *
 * Presentational — the caller decides what is disabled and why.
 */
const SavedCardOptionList: FC<Props> = ({
    cards,
    value,
    onChange,
    pageOptionDisabled = false,
    cardsDisabled = false,
    cardDisabledReason
}) => {
    // Row positions: 0 is "new card", card i is i + 1; -1 when nothing listed is selected.
    const cardIndex = cards.findIndex((card) => String(card._id) === value);
    const selectedIndex = value === PAGE_OPTION ? 0 : cardIndex < 0 ? -1 : cardIndex + 1;

    const divider = (index: number) => (
        <Box
            role="presentation"
            sx={{
                height: '1px',
                mx: `${ROW_PX}px`,
                // Kept in the flow when hidden, so selecting a row never shifts the list.
                bgcolor: selectedIndex === index || selectedIndex === index + 1 ? 'transparent' : 'divider'
            }}
        />
    );

    return (
        <RadioGroup
            aria-label="Card"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            sx={{ flexWrap: 'nowrap', gap: 0 }}
        >
            <OptionRow
                value={PAGE_OPTION}
                selected={value === PAGE_OPTION}
                disabled={pageOptionDisabled}
                ariaLabel="New card, pay on the payment page"
                tile={<NewCardTile />}
                lines={
                    <>
                        <Typography variant="body2" component="span" noWrap sx={{ ...lineSx, fontWeight: 600 }}>
                            New card
                        </Typography>
                        <Typography variant="body2" component="span" color="text.secondary" noWrap sx={lineSx}>
                            Pay on the payment page
                        </Typography>
                    </>
                }
            />

            {cards.map((card, index) => {
                const reason = cardDisabledReason(card);
                const rowDisabled = cardsDisabled || !!reason;
                return (
                    <Fragment key={card._id}>
                        {divider(index)}
                        <OptionRow
                            value={String(card._id)}
                            selected={value === String(card._id)}
                            disabled={rowDisabled}
                            tooltip={reason}
                            ariaLabel={savedCardAccessibleLabel(card)}
                            tile={
                                <CardTile
                                    size="medium"
                                    brand={card.brand}
                                    last4={card.last4}
                                    expired={card.isExpired}
                                />
                            }
                            lines={<CardLines card={card} muted={rowDisabled} />}
                        />
                    </Fragment>
                );
            })}
        </RadioGroup>
    );
};

export default SavedCardOptionList;
